import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AuthContext } from '../contexts/AuthContext';
import { listPlans } from '../utils/apiServices';
import { getJSON } from '../utils/errors';
import '../stylesheets/actionPlanView.css';
import '../stylesheets/pmvResultView.css';

const PmvResultView = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [planTitle, setPlanTitle] = useState('');
  const [reportMd, setReportMd] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user || !slug) return;

    const saved = getJSON(`pmv_result_${slug}`, null);
    if (!saved?.validation_md) {
      setError('Este plan aún no tiene un reporte de validación PMV generado.');
      setLoading(false);
      return;
    }

    setReportMd(saved.validation_md);

    listPlans()
      .then((data) => {
        const found = (data.results || []).find((p) => p.slug === slug);
        setPlanTitle(found?.title || slug);
      })
      .catch(() => setPlanTitle(slug))
      .finally(() => setLoading(false));
  }, [user, slug]);

  const handleBack = () => navigate('/plans/pmv/');
  const handlePrint = () => window.print();

  if (!user) return <Navigate to="/login/" replace />;

  if (loading) {
    return (
      <div className="action-plan-view">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Cargando reporte de validación...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="action-plan-view">
        <div className="error-container">
          <div className="error-icon">🧪</div>
          <h2>Sin reporte PMV</h2>
          <p>{error}</p>
          <button className="btn-back" onClick={handleBack}>
            Volver al editor PMV
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="action-plan-view">
      <div className="action-plan-header">
        <button className="btn-back" onClick={handleBack}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Volver al editor
        </button>
        <button className="btn-print no-print" onClick={handlePrint}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
            <rect x="6" y="14" width="12" height="8" />
          </svg>
          Imprimir
        </button>
      </div>

      <div className="action-plan-content">
        <div className="pmv-result-title-section">
          <div className="pmv-result-badge">Reporte de validación PMV</div>
          <h1>{planTitle}</h1>
          <div className="action-plan-meta">
            <span className="meta-item">
              <span className="meta-icon">🧪</span>
              Lean Startup · Build-Measure-Learn
            </span>
          </div>
        </div>

        <div className="action-plan-body">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ node, ...props }) => <h2 className="markdown-h1 pmv-md-h1" {...props} />,
              h2: ({ node, ...props }) => <h3 className="markdown-h2 pmv-md-h2" {...props} />,
              h3: ({ node, ...props }) => <h4 className="markdown-h3" {...props} />,
              ul: ({ node, ...props }) => <ul className="markdown-ul" {...props} />,
              ol: ({ node, ...props }) => <ol className="markdown-ol" {...props} />,
              li: ({ node, ...props }) => <li className="markdown-li" {...props} />,
              p: ({ node, ...props }) => <p className="markdown-p" {...props} />,
              strong: ({ node, ...props }) => <strong className="markdown-strong" {...props} />,
              em: ({ node, ...props }) => <em className="markdown-em" {...props} />,
              blockquote: ({ node, ...props }) => (
                <blockquote className="markdown-blockquote pmv-blockquote" {...props} />
              ),
              code: ({ node, inline, ...props }) =>
                inline ? (
                  <code className="markdown-code-inline" {...props} />
                ) : (
                  <code className="markdown-code-block" {...props} />
                ),
              table: ({ node, ...props }) => (
                <div className="pmv-table-wrapper">
                  <table className="pmv-table" {...props} />
                </div>
              ),
              th: ({ node, ...props }) => <th className="pmv-th" {...props} />,
              td: ({ node, ...props }) => <td className="pmv-td" {...props} />,
            }}
          >
            {reportMd}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
};

export default PmvResultView;
